import React, { useState, useEffect } from "react";
import { Landmark, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const ACCOUNT_KINDS = [
  { value: "corrente", label: "Conta Corrente" },
  { value: "poupanca", label: "Poupança" },
  { value: "aplicacao", label: "Aplicação / Investimento" },
  { value: "pix", label: "PIX / Chave Exclusiva" },
  { value: "caixa_fisico", label: "Caixa Físico / Dinheiro" },
  { value: "tronco", label: "Tronco de Beneficência" },
];

const COLOR_PALETTE = [
  "#D4AF37", "#A855F7", "#EF4444", "#10B981",
  "#3B82F6", "#F59E0B", "#EC4899", "#14B8A6",
];

export const BankAccountModal = ({ isOpen, onClose, onSubmit, initialData = null }) => {
  const [name, setName] = useState("");
  const [bankName, setBankName] = useState("");
  const [branch, setBranch] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountKind, setAccountKind] = useState("corrente");
  const [initialBalance, setInitialBalance] = useState("");
  const [color, setColor] = useState("#D4AF37");
  const [isActive, setIsActive] = useState(true);

  const isEdit = !!(initialData && initialData.id);

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || "");
      setBankName(initialData?.bank_name || "");
      setBranch(initialData?.branch || "");
      setAccountNumber(initialData?.account_number || "");
      setAccountKind(initialData?.account_kind || "corrente");
      setInitialBalance(
        initialData?.initial_balance != null && initialData?.initial_balance !== ""
          ? String(initialData.initial_balance)
          : ""
      );
      setColor(initialData?.color || "#D4AF37");
      setIsActive(initialData?.is_active !== false);
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Informe o nome da conta.");
      return;
    }
    onSubmit({
      id: initialData?.id || null,
      name: name.trim(),
      bank_name: bankName.trim(),
      branch: branch.trim(),
      account_number: accountNumber.trim(),
      account_kind: accountKind,
      initial_balance: parseFloat(initialBalance) || 0.0,
      color,
      is_active: isActive,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div
        data-testid="modal-bank-account-form"
        className="w-full max-w-lg bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            {isEdit ? (
              <Pencil className="w-5 h-5 text-[#D4AF37]" />
            ) : (
              <Landmark className="w-5 h-5 text-[#D4AF37]" />
            )}
            <h3 className="font-serif font-bold text-lg text-white">
              {isEdit ? "Editar Conta Bancária" : "Cadastrar Conta Bancária / Aplicação"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="btn-close-bank-account-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs text-slate-300 max-h-[75vh] overflow-y-auto">

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Nome da Conta *</label>
            <input
              data-testid="input-bank-account-name"
              type="text"
              required
              placeholder="Ex: Bradesco C/C Loja"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Banco / Instituição</label>
              <input
                data-testid="input-bank-account-bankname"
                type="text"
                placeholder="Bradesco, Itaú, Sicoob..."
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Tipo</label>
              <select
                data-testid="input-bank-account-kind"
                value={accountKind}
                onChange={(e) => setAccountKind(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                {ACCOUNT_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Agência</label>
              <input
                data-testid="input-bank-account-branch"
                type="text"
                placeholder="0001"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 font-mono text-xs"
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Nº da Conta</label>
              <input
                data-testid="input-bank-account-number"
                type="text"
                placeholder="12345-6"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 font-mono text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">
              Saldo Inicial (R$) {isEdit && <span className="text-slate-500 font-normal">— ajuste para conciliar com o extrato bancário</span>}
            </label>
            <input
              data-testid="input-bank-account-initial-balance"
              type="number"
              step="0.01"
              placeholder="0,00"
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              className="w-full bg-[#080C14] text-[#D4AF37] font-mono font-bold p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>

          <div>
            <label className="block mb-2 font-semibold text-slate-300">Cor do Card</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition ${color === c ? "border-white scale-110" : "border-transparent hover:border-slate-500"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`btn-color-${c}`}
                />
              ))}
            </div>
          </div>

          {isEdit && !initialData?.is_default && (
            <div className="flex items-center gap-2 pt-2">
              <input
                data-testid="input-bank-account-active"
                type="checkbox"
                id="ba-active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="ba-active" className="text-slate-300 cursor-pointer">
                Conta ativa (aparece no dropdown de novos lançamentos)
              </label>
            </div>
          )}

          <div className="pt-4 border-t border-slate-800 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs"
            >
              Cancelar
            </button>
            <button
              data-testid="btn-submit-bank-account"
              type="submit"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase shadow-[0_0_15px_rgba(212,175,55,0.3)]"
            >
              {isEdit ? "Salvar Alterações" : "Cadastrar Conta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
